/**
 * Die VBA-Prüfmakros des Excel-Kurses.
 *
 * Office.js deckt Excel gut ab – aber Sparklines, Szenarien, Gliederungen,
 * Konsolidierungen, PivotCharts, Zeitachsen, freigegebene Bereiche, das
 * Überwachungsfenster, Alternativtexte von Diagrammen und aufgezeichnete
 * Makros kommen darin nicht vor. Die Fensteransicht gibt es nur in
 * ExcelApiDesktop 1.1; dort ist das Makro der Ersatzweg.
 */

import { pruefmakro } from "@/office/makro/vba";

/** Gliederungsebenen der Zeilen je Blatt (Daten → Gruppieren, Teilergebnis). */
export const MAKRO_GLIEDERUNG = pruefmakro({
  pruefung: "excel-gliederung",
  host: "Excel",
  titel: "Gliederung auslesen",
  rumpf: [
    "Dim ws As Worksheet, zeile As Range, n As Long, ebenen As Long, gruppiert As Long",
    "For Each ws In ActiveWorkbook.Worksheets",
    "    n = n + 1",
    "    ebenen = 1",
    "    gruppiert = 0",
    "    For Each zeile In ws.UsedRange.Rows",
    "        If zeile.OutlineLevel > ebenen Then ebenen = zeile.OutlineLevel",
    "        If zeile.OutlineLevel > 1 Then gruppiert = gruppiert + 1",
    "    Next zeile",
    '    Wert "blatt." & n & ".name", ws.Name',
    '    Zahl "blatt." & n & ".ebenen", ebenen',
    '    Zahl "blatt." & n & ".gruppierteZeilen", gruppiert',
    "Next ws",
    'Zahl "blaetter", n',
  ],
});

/** Sparkline-Gruppen samt Typ und Markierung von Höchst- und Tiefstwert. */
export const MAKRO_SPARKLINES = pruefmakro({
  pruefung: "excel-sparklines",
  host: "Excel",
  titel: "Sparklines auslesen",
  rumpf: [
    "Dim ws As Worksheet, gruppe As Object, i As Long, n As Long",
    "For Each ws In ActiveWorkbook.Worksheets",
    "    For i = 1 To ws.Cells.SparklineGroups.Count",
    "        Set gruppe = ws.Cells.SparklineGroups(i)",
    "        n = n + 1",
    '        Wert "gruppe." & n & ".blatt", ws.Name',
    '        Wert "gruppe." & n & ".ort", gruppe.Location.Address(False, False)',
    '        Wert "gruppe." & n & ".quelle", gruppe.SourceData',
    '        Zahl "gruppe." & n & ".typ", gruppe.Type',
    '        Zahl "gruppe." & n & ".anzahl", gruppe.Count',
    '        JaNein "gruppe." & n & ".hoch", gruppe.Points.Highpoint.Visible',
    '        JaNein "gruppe." & n & ".tief", gruppe.Points.Lowpoint.Visible',
    "    Next i",
    "Next ws",
    'Zahl "gruppen", n',
  ],
});

/** VBA-Werte für `SparklineGroup.Type`. */
export const SPARKLINE_TYP: Record<number, string> = { 1: "Linie", 2: "Säule", 3: "Gewinn/Verlust" };

/** Szenarien aller Blätter (Daten → Was-wäre-wenn-Analyse → Szenario-Manager). */
export const MAKRO_SZENARIEN = pruefmakro({
  pruefung: "excel-szenarien",
  host: "Excel",
  titel: "Szenarien auslesen",
  rumpf: [
    "Dim ws As Worksheet, i As Long, n As Long",
    "For Each ws In ActiveWorkbook.Worksheets",
    "    For i = 1 To ws.Scenarios.Count",
    "        n = n + 1",
    '        Wert "szenario." & n & ".name", ws.Scenarios(i).Name',
    '        Wert "szenario." & n & ".blatt", ws.Name',
    '        Wert "szenario." & n & ".zellen", ws.Scenarios(i).ChangingCells.Address(False, False)',
    "    Next i",
    "Next ws",
    'Zahl "szenarien", n',
  ],
});

/** Konsolidierungen (Daten → Konsolidieren) samt Zahl der Quellbereiche. */
export const MAKRO_KONSOLIDIEREN = pruefmakro({
  pruefung: "excel-konsolidieren",
  host: "Excel",
  titel: "Konsolidierungen auslesen",
  rumpf: [
    "Dim ws As Worksheet, quellen As Variant, n As Long",
    "For Each ws In ActiveWorkbook.Worksheets",
    "    quellen = ws.ConsolidationSources",
    "    If IsArray(quellen) Then",
    "        n = n + 1",
    '        Wert "ziel." & n & ".blatt", ws.Name',
    '        Zahl "ziel." & n & ".quellen", UBound(quellen) - LBound(quellen) + 1',
    '        Zahl "ziel." & n & ".funktion", ws.ConsolidationFunction',
    "    End If",
    "Next ws",
    'Zahl "konsolidierungen", n',
  ],
});

/** PivotCharts, Zeitachsen und Datenschnitte. */
export const MAKRO_PIVOT = pruefmakro({
  pruefung: "excel-pivot",
  host: "Excel",
  titel: "PivotCharts und Zeitachsen auslesen",
  rumpf: [
    "Dim ws As Worksheet, diagramm As ChartObject, blattDiagramm As Chart, layout As Object",
    "Dim pivotDiagramme As Long, i As Long, zeitachsen As Long, datenschnitte As Long",
    "For Each ws In ActiveWorkbook.Worksheets",
    "    For Each diagramm In ws.ChartObjects",
    "        Set layout = Nothing",
    "        ' Bei gewöhnlichen Diagrammen gibt es kein PivotLayout.",
    "        On Error Resume Next",
    "        Set layout = diagramm.Chart.PivotLayout",
    "        On Error GoTo Fehler",
    "        If Not layout Is Nothing Then pivotDiagramme = pivotDiagramme + 1",
    "    Next diagramm",
    "Next ws",
    "For Each blattDiagramm In ActiveWorkbook.Charts",
    "    Set layout = Nothing",
    "    On Error Resume Next",
    "    Set layout = blattDiagramm.PivotLayout",
    "    On Error GoTo Fehler",
    "    If Not layout Is Nothing Then pivotDiagramme = pivotDiagramme + 1",
    "Next blattDiagramm",
    "For i = 1 To ActiveWorkbook.SlicerCaches.Count",
    "    If ActiveWorkbook.SlicerCaches(i).SlicerCacheType = xlTimeline Then",
    "        zeitachsen = zeitachsen + 1",
    "    Else",
    "        datenschnitte = datenschnitte + 1",
    "    End If",
    "Next i",
    'Zahl "pivotDiagramme", pivotDiagramme',
    'Zahl "zeitachsen", zeitachsen',
    'Zahl "datenschnitte", datenschnitte',
    'Zahl "pivotTabellen", PivotTabellenZaehlen()',
  ],
  hilfsfunktionen: [
    `
Private Function PivotTabellenZaehlen() As Long
    Dim ws As Worksheet
    For Each ws In ActiveWorkbook.Worksheets
        PivotTabellenZaehlen = PivotTabellenZaehlen + ws.PivotTables.Count
    Next ws
End Function`,
  ],
});

/** Freigegebene Bereiche (Bearbeitung von Bereichen zulassen), Notizen, Blattschutz. */
export const MAKRO_FREIGABE = pruefmakro({
  pruefung: "excel-freigabe",
  host: "Excel",
  titel: "Freigegebene Bereiche und Notizen auslesen",
  rumpf: [
    "Dim ws As Worksheet, i As Long, n As Long, notizen As Long, geschuetzt As Long",
    "For Each ws In ActiveWorkbook.Worksheets",
    "    For i = 1 To ws.Protection.AllowEditRanges.Count",
    "        n = n + 1",
    '        Wert "bereich." & n & ".blatt", ws.Name',
    '        Wert "bereich." & n & ".titel", ws.Protection.AllowEditRanges(i).Title',
    '        Wert "bereich." & n & ".adresse", ws.Protection.AllowEditRanges(i).Range.Address(False, False)',
    "    Next i",
    "    notizen = notizen + ws.Comments.Count",
    "    If ws.ProtectContents Then geschuetzt = geschuetzt + 1",
    "Next ws",
    'Zahl "bereiche", n',
    'Zahl "notizen", notizen',
    'Zahl "geschuetzteBlaetter", geschuetzt',
  ],
});

/** Fensteransicht, geteiltes Fenster, zweites Fenster, Überwachungsfenster. */
export const MAKRO_ANSICHT = pruefmakro({
  pruefung: "excel-ansicht",
  host: "Excel",
  titel: "Ansicht und Überwachungsfenster auslesen",
  rumpf: [
    'Zahl "ansicht", ActiveWindow.View',
    'JaNein "geteilt", ActiveWindow.Split',
    'Zahl "fenster", ActiveWorkbook.Windows.Count',
    'Zahl "ueberwachungen", Application.Watches.Count',
    'Zahl "berechnung", Application.Calculation',
  ],
});

/** VBA-Werte für `Window.View`. */
export const EXCEL_ANSICHT: Record<number, string> = {
  1: "normalView",
  2: "pageBreakPreview",
  3: "pageLayoutView",
};

/** Alternativtexte der Diagramme – die kennt Office.js nicht. */
export const MAKRO_DIAGRAMME = pruefmakro({
  pruefung: "excel-diagramme",
  host: "Excel",
  titel: "Alternativtexte der Diagramme auslesen",
  rumpf: [
    "Dim ws As Worksheet, form As Shape, n As Long",
    "For Each ws In ActiveWorkbook.Worksheets",
    "    For Each form In ws.Shapes",
    "        If form.HasChart Then",
    "            n = n + 1",
    '            Wert "diagramm." & n & ".blatt", ws.Name',
    '            Wert "diagramm." & n & ".name", form.Name',
    '            Wert "diagramm." & n & ".alternativtext", form.AlternativeText',
    "        End If",
    "    Next form",
    "Next ws",
    'Zahl "diagramme", n',
  ],
});

/**
 * Sucht ein aufgezeichnetes Makro „Kursmakro“ in allen geöffneten
 * Arbeitsmappen, auch in der persönlichen Makroarbeitsmappe (PERSONAL.XLSB).
 */
export const MAKRO_MAKROS = pruefmakro({
  pruefung: "excel-makros",
  host: "Excel",
  titel: "Aufgezeichnete Makros suchen",
  rumpf: [
    "Dim mappe As Workbook, anzahl As Long, zugriff As Boolean, ort As String",
    "On Error Resume Next",
    "anzahl = ActiveWorkbook.VBProject.VBComponents.Count",
    "zugriff = (Err.Number = 0)",
    "Err.Clear",
    "On Error GoTo Fehler",
    'JaNein "vbZugriff", zugriff',
    "If zugriff Then",
    "    For Each mappe In Application.Workbooks",
    "        If ProjektHatKursmakro(mappe.VBProject) Then ort = mappe.Name",
    "    Next mappe",
    "End If",
    'JaNein "kursmakro", Len(ort) > 0',
    'Wert "ort", ort',
  ],
  hilfsfunktionen: [
    `
Private Function ProjektHatKursmakro(ByVal projekt As Object) As Boolean
    ' Der Suchbegriff wird zusammengesetzt, damit dieses Prüfmakro sich nicht selbst findet.
    Dim komponente As Object, gesucht As String
    gesucht = "Sub " & "Kurs" & "makro"
    For Each komponente In projekt.VBComponents
        If komponente.CodeModule.CountOfLines > 0 Then
            If InStr(1, komponente.CodeModule.Lines(1, komponente.CodeModule.CountOfLines), _
                gesucht, vbTextCompare) > 0 Then ProjektHatKursmakro = True
        End If
    Next komponente
End Function`,
  ],
});
